<template>
    <vx-input
        data-test="hubspot-company-id-input"
        v-model="parsedId"
        :error="displayFirstError(validate.parsedId.$errors) || props.error"
    />
</template>
<script setup lang="ts">
import { computed, nextTick, type PropType } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength } from '@vuelidate/validators';
import { VxInput } from '@voxie/frontend-components';
import { displayFirstError } from '~/utils/validation';

const props = defineProps<{ error?: string }>();

const modelValue = defineModel({
    type: [String, null] as PropType<string | null>,
    default: null,
});

const parseHubspotCompanyId = (input: string): string | null => {
    const raw = input.trim();

    if (!raw) {
        return null;
    }

    // https?://<optional-region>.hubspot.com/contacts/<anything-non-slash>/record/0-2/<companyId>
    // "record/0-2" indicates it's a company resources
    const URL_RE =
        /^(?:https?:\/\/)?(?:[a-z0-9-]+\.)?hubspot\.com\/contacts\/[^/]+\/record\/0-2\/([A-Za-z0-9_-]+)(?:[/?#].*)?$/i;

    if (/^[A-Za-z0-9_-]+$/.test(raw)) {
        return raw;
    }

    return raw.match(URL_RE)?.[1] ?? raw;
};

const parsedId = computed({
    get: () => {
        return modelValue.value || '';
    },
    set: async (val: string) => {
        // Reset the value first to trigger reactivity even if the URL contains the same text parsed before.
        modelValue.value = modelValue.value ? null : val;
        await nextTick();
        modelValue.value = parseHubspotCompanyId(val) ?? null;
    },
});

const rules = computed(() => ({
    parsedId: {
        hubSpotUrl: helpers.withMessage(
            'The HubSpot Company ID or link to a HubSpot company record is not valid',
            (val: string) => val === '' || /^[A-Za-z0-9_-]+$/.test(val)
        ),
        maxLength: maxLength(40),
    },
}));

const validate = useVuelidate(rules, { parsedId });
</script>
